import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  FiUser,
  FiMenu,
  FiX,
  FiCalendar,
  FiShoppingBag,
  FiLogOut,
  FiBarChart2,
} from "react-icons/fi";
import { HiOutlineBanknotes } from "react-icons/hi2";
import CreateEvent from "./CreateEvent";
import ManageEvents from "./ManageEvents";
import ManageMerch from "./ManageMerch";
import AuthForm from "./AuthForm";
import EventDetails from "./EventDetails";
import EventFeed from "./EventFeed";
import MerchFeed from "./MerchFeed";
import MerchDetails from "./MerchDetails";
import PaymentInfo from "./PaymentInfo";
import EarningsDashboard from "./EarningsDashboard";

function AppLayout() {
  const [events, setEvents] = useState([]);
  const [merch, setMerch] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const savedEmail = localStorage.getItem("verifiedEmail");
    const savedAuth = localStorage.getItem("isAuthenticated");

    if (savedEmail && savedAuth === "true") {
      setVerifiedEmail(savedEmail);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch("/api/loadEvents");
        const data = await response.json();
        setEvents(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Failed to load events:", error);
        setEvents([]);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const fetchMerch = async () => {
      try {
        const response = await fetch("/api/loadMerch");
        const data = await response.json();
        setMerch(Array.isArray(data) ? data : data.items || []);
      } catch (error) {
        console.error("Failed to load merch:", error);
        setMerch([]);
      }
    };

    fetchMerch();
  }, []);

  const refreshMerch = async () => {
    try {
      const response = await fetch("/api/loadMerch");
      const data = await response.json();
      setMerch(Array.isArray(data) ? data : data.items || []);
    } catch (error) {
      console.error("Failed to refresh merch:", error);
    }
  };

  const handleVerified = (email) => {
    setIsAuthenticated(true);
    setVerifiedEmail(email);
    setShowAuth(false);
    setMenuOpen(false);

    localStorage.setItem("isAuthenticated", "true");
    localStorage.setItem("verifiedEmail", email);

    navigate("/manage-events");
  };

  const handleLoginClick = () => {
    setShowAuth(true);
    setMenuOpen(false);
  };

  const handleMenuNavigate = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("verifiedEmail");

    setIsAuthenticated(false);
    setVerifiedEmail("");
    setMenuOpen(false);
    setShowAuth(false);

    navigate("/events");
  };

  const isDashboardRoute =
    location.pathname === "/manage-events" ||
    location.pathname === "/create-event" ||
    location.pathname === "/manage-merch" ||
    location.pathname === "/payment-info" ||
    location.pathname === "/earnings";
  
  const navLinkClass = (isActive) =>
    `rounded-full px-4 py-2 text-sm font-semibold transition ${
      isActive
        ? "bg-white text-[#123524] shadow"
        : "text-white/80 hover:bg-white/10 hover:text-white"
    }`;

  const menuItemClass =
    "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:bg-emerald-50 hover:text-[#123524]";

  return (
    <>
      {showAuth && (
        <AuthForm
          onVerified={handleVerified}
          onCancel={() => setShowAuth(false)}
        />
      )}

      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-emerald-100">
        <header className="sticky top-0 z-20 border-b border-emerald-950/20 bg-[#123524] shadow-sm">
          <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-3 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4">
              <Link
                to="/events"
                className="flex items-center"
                aria-label="Onstage Home"
              >
                   <Link
                      to="/events"
                      className="flex h-16 w-[220px] items-center sm:h-20 sm:w-[280px]"
                      aria-label="Onstage Home"
                    >
                    </Link>
              </Link>

              <div className="relative">
                {!isAuthenticated ? (
                  <button
                    onClick={handleLoginClick}
                    className="rounded-full bg-white p-3 text-[#123524] shadow-lg transition hover:bg-emerald-50"
                    title="Log in"
                    aria-label="Log in"
                  >
                    <FiUser className="h-6 w-6" />
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setMenuOpen((prev) => !prev)}
                      className="rounded-full bg-white p-3 text-[#123524] shadow-lg transition hover:bg-emerald-50"
                      title="Open menu"
                      aria-label="Open menu"
                    >
                      {menuOpen ? (
                        <FiX className="h-6 w-6" />
                      ) : (
                        <FiMenu className="h-6 w-6" />
                      )}
                    </button>

                    {menuOpen && (
                      <div className="absolute right-0 mt-3 w-64 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl">
                        <div className="border-b border-slate-100 px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                            Signed in as
                          </p>
                          <p className="truncate text-sm font-semibold text-slate-700">
                            {verifiedEmail}
                          </p>
                        </div>

                        <div className="p-2">
                          <button
                            onClick={() => handleMenuNavigate("/manage-events")}
                            className={menuItemClass}
                          >
                            <FiCalendar className="h-5 w-5" />
                            Manage Events
                          </button>

                          <button
                              onClick={() => handleMenuNavigate("/earnings")}
                              className={menuItemClass}
                            >
                              <FiBarChart2 className="h-5 w-5" />
                              Earnings Dashboard
                            </button>

                          <button
                            onClick={() => handleMenuNavigate("/manage-merch")}
                            className={menuItemClass}
                          >
                            <FiShoppingBag className="h-5 w-5" />
                            Manage Merch
                          </button>

                          <button
                            onClick={() => handleMenuNavigate("/payment-info")}
                            className={menuItemClass}
                          >
                            <HiOutlineBanknotes className="h-5 w-5" />
                            Payment Info
                          </button>

                          <button
                            onClick={handleLogout}
                            className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50"
                          >
                            <FiLogOut className="h-5 w-5" />
                            Log Out
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {!isDashboardRoute && (
              <nav className="flex flex-wrap items-center gap-3">
                <Link
                  to="/events"
                  className={navLinkClass(
                    location.pathname === "/" ||
                      location.pathname === "/events" ||
                      location.pathname.startsWith("/events/")
                  )}
                >
                  Events
                </Link>

                <Link
                  to="/merch"
                  className={navLinkClass(
                    location.pathname === "/merch" ||
                      location.pathname.startsWith("/merch/")
                  )}
                >
                  Merch
                </Link>
              </nav>
            )}
          </div>
        </header>

        <Routes>
          <Route path="/" element={<Navigate to="/events" replace />} />

          <Route
            path="/events"
            element={<EventFeed events={events} merchItems={merch} />}
          />


          <Route
            path="/earnings"
            element={
              isAuthenticated ? (
                <EarningsDashboard userEmail={verifiedEmail} />
              ) : (
                <Navigate to="/events" replace />
              )
            }
          />

          <Route
            path="/events/:eventId"
            element={<EventDetails events={events} merch={merch} />}
          />

          <Route
            path="/merch"
            element={<MerchFeed merch={merch} events={events} />}
          />

          <Route
            path="/merch/:merchId"
            element={<MerchDetails merch={merch} events={events} />}
          />

          <Route
            path="/manage-events"
            element={
              isAuthenticated ? (
                <ManageEvents
                  userEmail={verifiedEmail}
                  onCreateEvent={() => navigate("/create-event")}
                  onManageMerch={() => navigate("/manage-merch")}
                />
              ) : (
                <Navigate to="/events" replace />
              )
            }
          />

          <Route
            path="/manage-merch"
            element={
              isAuthenticated ? (
                <ManageMerch
                  userEmail={verifiedEmail}
                  events={events}
                  onRefreshMerch={refreshMerch}
                />
              ) : (
                <Navigate to="/events" replace />
              )
            }
          />

          <Route
            path="/create-event"
            element={
              isAuthenticated ? (
                <CreateEvent
                  userEmail={verifiedEmail}
                  onBack={() => navigate("/manage-events")}
                />
              ) : (
                <Navigate to="/events" replace />
              )
            }
          />

          <Route
            path="/payment-info"
            element={
              isAuthenticated ? (
                <PaymentInfo userEmail={verifiedEmail} />
              ) : (
                <Navigate to="/events" replace />
              )
            }
          />
        </Routes>
      </div>
    </>
  );
}

export default function App() {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
}